import React, { useState, useEffect } from "react";
import { supabase } from "./supabase";
import { userService, elogioService } from "./services/supabaseService";
import Toast from "./components/Toast";
import "./style.css";

const DEFAULT_AVATAR = "https://i.pinimg.com/736x/d4/8f/8f/d48f8f59f2de25750b13084b59301201.jpg";

// Função para obter cor da universidade
const getUniversityColor = (university) => {
  const colors = {
    'UNIVALI': '#1e3d72',
    'FURB': '#8B0000', 
    'UFSC': '#003366',
    'UDESC': '#2F4F4F',
    'UNISUL': '#006400',
    'UNOESC': '#4B0082',
    'UNESC': '#8B4513',
    'UNIFEBE': '#FF8C00',
    'UNIASSELVI': '#DC143C',
    'FACVEST': '#9ACD32',
    'UNIDAVI': '#FF1493',
    'FAMEBLU': '#00CED1',
    'UNIPLAC': '#FF6347',
    'USC': '#9932CC',
    'IFC': '#228B22',
    'IFSC': '#4682B4',
    'CEDUP': '#D2691E',
    'SENAI': '#FF4500',
    'SENAC': '#32CD32',
    'FATMA': '#FF69B4',
    'ESTÁCIO': '#20B2AA',
    'UNOPAR': '#FF7F50',
    'ANHEMBI': '#DA70D6'
  };
  
  if (!university) return '#666';
  
  const upperUni = university.toUpperCase();
  for (const [key, color] of Object.entries(colors)) {
    if (upperUni.includes(key)) {
      return color;
    }
  }
  return '#666';
};

function UserProfile({ userId }) {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [isFriend, setIsFriend] = useState(false);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [showElogioModal, setShowElogioModal] = useState(false);
  const [selectedElogio, setSelectedElogio] = useState("");
  const [sendingElogio, setSendingElogio] = useState(false);
  const [toast, setToast] = useState(null);
  const [friendsCount, setFriendsCount] = useState(0);
  const [hasReceivedRequest, setHasReceivedRequest] = useState(false);
  const [receivedRequestId, setReceivedRequestId] = useState(null);

  const showToast = (message, type = 'info', userPhoto = null) => {
    setToast({ message, type, photo: userPhoto });
  };

  useEffect(() => {
    loadUserData();
    checkIfFriend();
    checkPendingRequest();
    checkReceivedRequest();
    loadFriendsCount();
  }, [userId]);

  const loadUserData = async () => {
    if (!userId) {
      console.warn('⚠️ UserProfile: userId não fornecido');
      setLoading(false);
      return;
    }
    
    try {
      console.log('👤 UserProfile: Carregando dados do usuário:', userId);
      const userData = await userService.getUserById(userId);
      if (userData) {
        console.log('✅ UserProfile: Dados carregados:', userData);
        
        // Buscar elogios do usuário
        const elogios = await elogioService.getUserElogios(userId);
        
        setUserData({
          ...userData,
          elogios
        });
      } else {
        console.warn('⚠️ UserProfile: Usuário não encontrado');
      }
    } catch (err) {
      console.error("💥 UserProfile: Erro ao carregar perfil:", err);
      // Mostrar erro específico para o usuário
      setUserData({ error: err.message || 'Erro ao carregar perfil' });
    } finally {
      setLoading(false);
    }
  };

  const checkIfFriend = async () => {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser || !userId) return;

      const status = await userService.checkFriendship(currentUser.id, userId);
      setIsFriend(status === 'accepted');
    } catch (err) {
      console.error("Erro ao verificar amizade:", err);
    }
  };

  const checkPendingRequest = async () => {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser || !userId) return;

      const status = await userService.checkFriendship(currentUser.id, userId);
      setHasPendingRequest(status === 'pending');
    } catch (err) {
      console.error("Erro ao verificar solicitação pendente:", err);
    }
  };

  const checkReceivedRequest = async () => {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser || !userId) return;

      // Verificar se recebeu solicitação (olhar do userId para currentUser)
      const status = await userService.checkFriendship(userId, currentUser.id);
      setHasReceivedRequest(status === 'pending');
    } catch (err) {
      console.error("Erro ao verificar solicitação recebida:", err);
    }
  };

  const loadFriendsCount = async () => {
    if (!userId) return;
    try {
      const count = await userService.getFriendsCount(userId);
      setFriendsCount(count);
    } catch (err) {
      console.error("Erro ao carregar contagem de amigos:", err);
      setFriendsCount(0);
    }
  };

  const handleFriendRequest = async () => {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser || !userId) return;

      setSendingRequest(true);
      
      const success = await userService.sendFriendRequest(currentUser.id, userId);
      
      if (success) {
        setHasPendingRequest(true);
        showToast("Solicitação enviada", "success", userData.avatar || DEFAULT_AVATAR);
      } else {
        showToast("Erro ao enviar solicitação", "error");
      }
    } catch (err) {
      console.error("Erro ao enviar solicitação:", err);
      showToast("Erro ao enviar solicitação", "error");
    }
    setSendingRequest(false);
  };

  const handleCancelRequest = async () => {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser || !userId) return;

      setSendingRequest(true);
      
      const success = await userService.removeFriendship(currentUser.id, userId);
      
      if (success) {
        setHasPendingRequest(false);
        showToast("Solicitação cancelada", "info", userData.avatar || DEFAULT_AVATAR);
      } else {
        showToast("Erro ao cancelar", "error");
      }
    } catch (err) {
      console.error("Erro ao cancelar solicitação:", err);
      showToast("Erro ao cancelar", "error");
    }
    setSendingRequest(false);
  };

  const acceptFriendRequest = async () => {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser || !userId) return;

      setSendingRequest(true);
      
      const success = await userService.acceptFriendRequest(currentUser.id, userId);
      
      if (success) {
        setHasReceivedRequest(false);
        setIsFriend(true);
        showToast("Solicitação aceita! Vocês agora são amigos", "success", userData.avatar || DEFAULT_AVATAR);
        loadFriendsCount();
      } else {
        showToast("Erro ao aceitar solicitação", "error");
      }
    } catch (err) {
      console.error("Erro ao aceitar solicitação:", err);
      showToast("Erro ao aceitar solicitação", "error");
    }
    setSendingRequest(false);
  };

  const rejectFriendRequest = async () => {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser || !userId) return;

      setSendingRequest(true);
      
      const success = await userService.removeFriendship(userId, currentUser.id);
      
      if (success) {
        setHasReceivedRequest(false);
        showToast("Solicitação recusada", "info", userData.avatar || DEFAULT_AVATAR);
      } else {
        showToast("Erro ao recusar solicitação", "error");
      }
    } catch (err) {
      console.error("Erro ao recusar solicitação:", err);
      showToast("Erro ao recusar solicitação", "error");
    }
    setSendingRequest(false);
  };

  const handleElogioClick = async (elogioType) => {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      
      // Verificar se está tentando elogiar a si mesmo
      if (currentUser && currentUser.id === userId) {
        showToast("Você não pode elogiar a si mesmo", "warning");
        return;
      }
      
      setSelectedElogio(elogioType);
      setShowElogioModal(true);
    } catch (err) {
      console.error("Erro ao abrir modal de elogio:", err);
    }
  };

  const handleSendElogio = async () => {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser || !userId || !selectedElogio) return;

      setSendingElogio(true);
      
      // Verificar se já elogiou hoje
      const canSend = await elogioService.checkCanSendElogio(currentUser.id, userId, selectedElogio);
      if (!canSend) {
        showToast("Você já enviou esse elogio hoje!", "warning");
        setSendingElogio(false);
        setShowElogioModal(false);
        return;
      }
      
      // Enviar elogio
      await elogioService.sendElogio(currentUser.id, userId, selectedElogio);
      
      // Atualizar contagem local
      setUserData(prev => ({
        ...prev,
        elogios: {
          ...prev.elogios,
          [selectedElogio]: (prev.elogios?.[selectedElogio] || 0) + 1
        }
      }));
      
      setShowElogioModal(false);
      setSelectedElogio("");
      showToast("Elogio enviado com sucesso!", "success", userData.avatar || DEFAULT_AVATAR);
    } catch (err) {
      console.error("Erro ao enviar elogio:", err);
      showToast("Erro ao enviar elogio", "error");
    }
    setSendingElogio(false);
  };

  if (loading) {
    return (
      <div className="orkut-profile-view">
        <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
          Carregando perfil...
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="orkut-profile-view">
        <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
          Perfil não encontrado.
        </div>
      </div>
    );
  }



  const elogios = userData.elogios || {};

  return (
    <div className="orkut-profile-view">
      {/* Cabeçalho do Perfil */}
      <div className="orkut-profile-header-view">
        <div className="orkut-profile-header-top">
          <div 
            className="orkut-profile-avatar-large"
            style={{
              backgroundImage: `url(${userData.avatar || DEFAULT_AVATAR})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
          <div className="orkut-profile-header-info">
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '8px' }}>
              <h2 className="orkut-profile-nome" style={{ margin: 0, lineHeight: 1.2 }}>{userData.nome || "Usuário"}</h2>
              {userData.verified && (
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="#1DA1F2"
                  className="verified-badge"
                  title="Perfil Verificado"
                  style={{ marginLeft: '2px', flexShrink: 0, marginTop: '2px' }}
                >
                  <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91s-2.52-1.27-3.91-.81c-.66-1.31-1.91-2.19-3.34-2.19s-2.67.88-3.34 2.19c-1.39-.46-2.9-.2-3.91.81s-1.27 2.52-.81 3.91c-1.31.66-2.19 1.91-2.19 3.34s.88 2.67 2.19 3.34c-.46 1.39-.2 2.9.81 3.91s2.52 1.27 3.91.81c.66 1.31 1.91 2.19 3.34 2.19s2.67-.88 3.34-2.19c1.39.46 2.9.2 3.91-.81s1.27-2.52.81-3.91c1.31-.66 2.19-1.91 2.19-3.34zm-11.71 4.2L6.8 12.46l1.41-1.42L10.54 13.38l5.65-5.65 1.42 1.42-7.12 7.05z"/>
                </svg>
              )}
            </div>
            {userData.universidade && (
              <p className="orkut-profile-university">
                <span 
                  className="university-indicator" 
                  style={{ color: getUniversityColor(userData.universidade) }}
                >
                  {userData.universidade}
                </span>
              </p>
            )}
            {userData.status && (
              <p className="orkut-profile-status-text">"{userData.status}"</p>
            )}
            {userData.cidade && (
              <p className="orkut-profile-location">📍 {userData.cidade}</p>
            )}
          </div>
        </div>
        
        <div className="orkut-profile-stats">
          <div className="orkut-profile-stat-item">
            <span className="orkut-stat-number">{friendsCount}</span>
            <span className="orkut-stat-label">amigos</span>
          </div>
          <div className="orkut-profile-stat-item">
            <span className="orkut-stat-number">{Object.keys(elogios).reduce((sum, key) => sum + (elogios[key] || 0), 0)}</span>
            <span className="orkut-stat-label">elogios</span>
          </div>
        </div>
      </div>

      {/* Botão de Solicitar Amizade ou Status de Amigo */}
      <div style={{ marginBottom: '16px' }}>
        {isFriend ? (
          <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
            <button 
              className="orkut-friend-status-btn"
              disabled
            >
              ✓ Amigos
            </button>
            <button 
              className="orkut-send-message-btn"
              onClick={() => {
                // Envia mensagem para abrir chat
                window.postMessage({ 
                  type: 'openChat', 
                  userId: userId, 
                  userData: { ...userData, userId: userId }
                }, '*');
                
                // Envia mensagem para fechar modal (apenas no mobile vai direto para o chat)
                const isMobile = window.innerWidth <= 768;
                if (!isMobile) {
                  // No desktop, fecha o modal e pode mostrar toast
                  window.postMessage({ 
                    type: 'closeModal'
                  }, '*');
                } else {
                  // No mobile, fecha modal sem toast - vai direto para conversa
                  window.postMessage({ 
                    type: 'closeModalSilent'
                  }, '*');
                }
              }}
            >
              Enviar Mensagem
            </button>
          </div>
        ) : hasReceivedRequest ? (
          <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                className="orkut-accept-request-btn"
                onClick={acceptFriendRequest}
                disabled={sendingRequest}
              >
                {sendingRequest ? "Aceitando..." : "✓ Aceitar Solicitação"}
              </button>
              <button 
                className="orkut-reject-request-btn"
                onClick={rejectFriendRequest}
                disabled={sendingRequest}
              >
                {sendingRequest ? "Recusando..." : "✗ Recusar"}
              </button>
            </div>
          </div>
        ) : hasPendingRequest ? (
          <button 
            className="orkut-friend-pending-btn"
            onClick={handleCancelRequest}
            disabled={sendingRequest}
          >
            {sendingRequest ? "Cancelando..." : "⏳ Solicitação Enviada (cancelar)"}
          </button>
        ) : (
          <button 
            className="orkut-friend-request-btn"
            onClick={handleFriendRequest}
            disabled={sendingRequest}
          >
            {sendingRequest ? "Enviando..." : "👥 Solicitar Amizade"}
          </button>
        )}
      </div>

      {/* Seção de Elogios - sempre visível */}
      <div className="orkut-elogios-section">
        <h3 className="orkut-section-title">✨ Elogios (clique para enviar)</h3>
        <div className="orkut-elogios-grid">
          <div 
            className="orkut-elogio-card clickable"
            onClick={() => handleElogioClick('legal')}
          >
            <span className="orkut-elogio-label">⭐ Legal</span>
            <span className="orkut-elogio-nota-big">{elogios.legal || 0}</span>
          </div>
          <div 
            className="orkut-elogio-card clickable"
            onClick={() => handleElogioClick('confiavel')}
          >
            <span className="orkut-elogio-label">💙 Confiável</span>
            <span className="orkut-elogio-nota-big">{elogios.confiavel || 0}</span>
          </div>
          <div 
            className="orkut-elogio-card clickable"
            onClick={() => handleElogioClick('criativo')}
          >
            <span className="orkut-elogio-label">🎨 Criativo</span>
            <span className="orkut-elogio-nota-big">{elogios.criativo || 0}</span>
          </div>
          <div 
            className="orkut-elogio-card clickable"
            onClick={() => handleElogioClick('inteligente')}
          >
            <span className="orkut-elogio-label">🧠 Inteligente</span>
            <span className="orkut-elogio-nota-big">{elogios.inteligente || 0}</span>
          </div>
          <div 
            className="orkut-elogio-card clickable"
            onClick={() => handleElogioClick('simpatico')}
          >
            <span className="orkut-elogio-label">😊 Simpático</span>
            <span className="orkut-elogio-nota-big">{elogios.simpatico || 0}</span>
          </div>
        </div>
      </div>

      {/* Quem sou eu - apenas se existir */}
      {userData.quemsoueu && (
        <div className="orkut-profile-section">
          <h3 className="orkut-section-title">💭 Quem sou eu</h3>
          <div className="orkut-profile-text-box">
            {userData.quemsoueu}
          </div>
        </div>
      )}

      {/* Informações - apenas campos preenchidos */}
      {(userData.interesses || userData.curso || userData.universidade || userData.idiomas || userData.humor || userData.aniversario) && (
        <div className="orkut-profile-section">
          <h3 className="orkut-section-title">📋 Informações</h3>
          <div className="orkut-info-list">
            {userData.curso && (
              <div className="orkut-info-item">
                <span className="orkut-info-label">Curso:</span>
                <span className="orkut-info-value">{userData.curso}</span>
              </div>
            )}
            {userData.universidade && (
              <div className="orkut-info-item">
                <span className="orkut-info-label">Universidade:</span>
                <span className="orkut-info-value">
                  {userData.universidade}
                </span>
              </div>
            )}
            {userData.periodo && (
              <div className="orkut-info-item">
                <span className="orkut-info-label">Período:</span>
                <span className="orkut-info-value">{userData.periodo}</span>
              </div>
            )}
            {userData.interesses && (
              <div className="orkut-info-item">
                <span className="orkut-info-label">Interesses:</span>
                <span className="orkut-info-value">{userData.interesses}</span>
              </div>
            )}
            {userData.aniversario && (
              <div className="orkut-info-item">
                <span className="orkut-info-label">Aniversário:</span>
                <span className="orkut-info-value">{userData.aniversario}</span>
              </div>
            )}
            {userData.idiomas && (
              <div className="orkut-info-item">
                <span className="orkut-info-label">Idiomas:</span>
                <span className="orkut-info-value">{userData.idiomas}</span>
              </div>
            )}
            {userData.humor && (
              <div className="orkut-info-item">
                <span className="orkut-info-label">Humor:</span>
                <span className="orkut-info-value">{userData.humor}</span>
              </div>
            )}
            {userData.animais && (
              <div className="orkut-info-item">
                <span className="orkut-info-label">Animais:</span>
                <span className="orkut-info-value">{userData.animais}</span>
              </div>
            )}
            {userData.moro && (
              <div className="orkut-info-item">
                <span className="orkut-info-label">Moro:</span>
                <span className="orkut-info-value">{userData.moro}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de Elogio */}
      {showElogioModal && (
        <div className="orkut-modal-bg" onClick={() => setShowElogioModal(false)}>
          <div className="orkut-modal" onClick={(e) => e.stopPropagation()}>
            <div className="orkut-modal-header">
              <h3>✨ Enviar Elogio</h3>
              <button onClick={() => setShowElogioModal(false)}>✕</button>
            </div>
            <div className="orkut-modal-content">
              <div className="orkut-elogio-confirm">
                <p>Você deseja enviar o elogio:</p>
                <div className="orkut-elogio-selected">
                  {selectedElogio === 'legal' && '⭐ Legal'}
                  {selectedElogio === 'confiavel' && '💙 Confiável'}
                  {selectedElogio === 'criativo' && '🎨 Criativo'}
                  {selectedElogio === 'inteligente' && '🧠 Inteligente'}
                  {selectedElogio === 'simpatico' && '😊 Simpático'}
                </div>
                <p>para <strong>{userData.nome || "este usuário"}</strong>?</p>
                <div className="orkut-elogio-actions">
                  <button 
                    className="orkut-btn-primary"
                    onClick={handleSendElogio}
                    disabled={sendingElogio}
                  >
                    {sendingElogio ? "Enviando..." : "Confirmar"}
                  </button>
                  <button 
                    className="orkut-btn-secondary"
                    onClick={() => setShowElogioModal(false)}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Profissional */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          photo={toast.photo}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default UserProfile;
